import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import {
  RocketLaunchIcon, ArrowClockwiseIcon, PulseIcon, WarningIcon,
  ProhibitIcon, PlayCircleIcon, TrendUpIcon, TrendDownIcon,
  LinkSimpleIcon, CheckCircleIcon, ChartLineIcon,
} from "@phosphor-icons/react";

const STATUS_META = {
  PENDING:  { color: "#FFB400", label: "PENDING",  Icon: ArrowClockwiseIcon, hint: "Waiting for VPS to clone cBot" },
  RUNNING:  { color: "#D6FF38", label: "RUNNING",  Icon: PulseIcon,          hint: "Live in cTrader terminal" },
  KILLED:   { color: "#FF4D4D", label: "KILLED",   Icon: ProhibitIcon,       hint: "Kill switch tripped or manual stop" },
};

const DEFAULT_META = { color: "#64748B", label: "UNKNOWN", Icon: WarningIcon, hint: "" };

function StatCard({ label, value, sub, tone }) {
  const color = tone === "profit" ? "#2ECA7F" : tone === "loss" ? "#FF4D4D" : "#F1F5F9";
  return (
    <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-3">
      <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">{label}</div>
      <div className="mono text-2xl mt-1" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] mono text-[#94A3B8] mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * Tiny inline SVG sparkline. Draws a min/max-normalized polyline of a
 * numeric series with a subtle area fill. Colored green if net-positive,
 * red if net-negative, neutral if flat.
 */
function Sparkline({ points, width = 96, height = 28 }) {
  if (!points || points.length < 2) {
    return <div className="text-[10px] mono text-[#4A5061]" title="not enough history yet">—</div>;
  }
  const vals = points.map((p) => Number(p.pnl || 0));
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const stepX = width / (vals.length - 1);
  const coords = vals.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const first = vals[0];
  const last = vals[vals.length - 1];
  const delta = last - first;
  const stroke = delta > 0 ? "#2ECA7F" : delta < 0 ? "#FF4D4D" : "#94A3B8";
  const fill = delta > 0 ? "rgba(46,202,127,0.12)" : delta < 0 ? "rgba(255,77,77,0.12)" : "rgba(148,163,184,0.10)";
  const areaPath = `M0,${height} L${coords.replaceAll(" ", " L")} L${width},${height} Z`;
  return (
    <svg width={width} height={height} className="block" data-testid="live-sparkline">
      <path d={areaPath} fill={fill} stroke="none" />
      <polyline points={coords} fill="none" stroke={stroke} strokeWidth="1.4" />
    </svg>
  );
}

/**
 * Portfolio Risk snapshot: aggregates all RUNNING deployments to expose
 * concentration + correlation risk at a glance (V2 P4 - lightweight
 * Portfolio Manager). Nothing here calls the backend; everything is
 * derived client-side from the deployments payload.
 */
function PortfolioRiskCard({ deployments }) {
  const running = deployments.filter((d) => d.status === "RUNNING");

  const stats = useMemo(() => {
    if (running.length === 0) return null;
    let exposure = 0;
    let worst = null;
    const bySym = new Map();
    for (const d of running) {
      const bal = Number(d.live_balance ?? d.current_balance ?? 0);
      const start = Number(d.starting_balance ?? 1000);
      exposure += bal;
      const dd = start > 0 ? ((start - bal) / start) * 100 : 0;
      if (!worst || dd > worst.dd) worst = { name: d.bot_name, dd, botId: d.bot_id };
      const sym = d.symbol || "UNKNOWN";
      bySym.set(sym, (bySym.get(sym) || 0) + bal);
    }
    const symbols = [...bySym.entries()]
      .map(([sym, bal]) => ({ sym, bal, pct: exposure ? (bal / exposure) * 100 : 0 }))
      .sort((a, b) => b.pct - a.pct);
    const symConcentration = symbols[0]?.pct || 0;
    // Correlation warning: 3+ bots on any single symbol
    const symCounts = new Map();
    for (const d of running) {
      const s = d.symbol || "UNKNOWN";
      symCounts.set(s, (symCounts.get(s) || 0) + 1);
    }
    const overlaps = [...symCounts.entries()].filter(([, n]) => n >= 3);
    return { exposure, worst, symbols: symbols.slice(0, 4), symConcentration, overlaps };
  }, [running]);

  if (!stats) return null;

  const concTone = stats.symConcentration >= 60 ? "#FF4D4D" : stats.symConcentration >= 40 ? "#F97316" : "#2ECA7F";
  const worstDdTone = stats.worst.dd >= 15 ? "#FF4D4D" : stats.worst.dd >= 8 ? "#F97316" : "#2ECA7F";

  return (
    <div
      data-testid="portfolio-risk-card"
      className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <ChartLineIcon size={14} className="text-[#D6FF38]" />
        <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/80">
          Portfolio Risk · {running.length} live bots
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-[9px] uppercase text-[#64748B] tracking-widest">Total Exposure</div>
          <div className="mono text-lg text-[#F1F5F9]" data-testid="risk-total-exposure">
            {fmt.money(stats.exposure)}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-[#64748B] tracking-widest">Symbol Concentration</div>
          <div className="mono text-lg" style={{ color: concTone }} data-testid="risk-concentration">
            {stats.symConcentration.toFixed(0)}%
          </div>
          <div className="text-[10px] mono text-[#94A3B8]">
            in {stats.symbols[0]?.sym || "—"}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-[#64748B] tracking-widest">Worst Drawdown</div>
          <div className="mono text-lg" style={{ color: worstDdTone }} data-testid="risk-worst-dd">
            −{stats.worst.dd.toFixed(1)}%
          </div>
          <div className="text-[10px] mono text-[#94A3B8] truncate" title={stats.worst.name}>
            {stats.worst.name}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-[#64748B] tracking-widest">Symbol Mix</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {stats.symbols.map((s) => (
              <span
                key={s.sym}
                className="text-[10px] mono px-1.5 py-0.5 rounded bg-[#0B0C10] border border-[#2A2D3A] text-[#94A3B8]"
                title={`${fmt.money(s.bal)} exposure`}
              >
                {s.sym} {s.pct.toFixed(0)}%
              </span>
            ))}
          </div>
        </div>
      </div>
      {stats.overlaps.length > 0 && (
        <div
          data-testid="risk-correlation-warning"
          className="mt-3 text-[11px] mono border border-[#F97316]/30 bg-[#F97316]/5 text-[#F97316] rounded-md px-2 py-1.5"
        >
          <WarningIcon size={12} weight="fill" className="inline mr-1" />
          Correlation warning: {stats.overlaps.map(([s, n]) => `${n} bots on ${s}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

export default function LiveTrading() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [ctStatus, setCtStatus] = useState(null);
  const [historyByBot, setHistoryByBot] = useState({});   // V2 P6 - per-bot PnL history for sparklines
  const location = useLocation();

  const fetchDeployments = useCallback(() => {
    api.get("/live/deployments")
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, deployments: [], running_count: 0, killed_count: 0, total_pnl: 0, total_balance: 0 }));
  }, []);

  const fetchOauthStatus = useCallback(() => {
    api.get("/oauth/spotware/status")
      .then((r) => setOauthStatus(r.data))
      .catch(() => setOauthStatus({ configured: false, linked_count: 0 }));
  }, []);

  const fetchCtStatus = useCallback(() => {
    api.get("/ctrader/status")
      .then((r) => setCtStatus(r.data))
      .catch(() => setCtStatus({ available: false }));
  }, []);

  useEffect(() => {
    fetchDeployments();
    fetchOauthStatus();
    fetchCtStatus();
    const t = setInterval(() => {
      fetchDeployments();
      fetchCtStatus();
    }, 5000);
    return () => clearInterval(t);
  }, [fetchDeployments, fetchOauthStatus, fetchCtStatus]);

  // V2 P6 - lazy-load per-bot PnL history for sparklines every ~30s so
  // running deployments show a tiny trend chart in-row. Only fetches
  // for RUNNING or KILLED (skips PENDING - no history yet).
  useEffect(() => {
    if (!data?.deployments?.length) return;
    const trackable = data.deployments.filter(
      (d) => d.status === "RUNNING" || d.status === "KILLED"
    );
    let cancelled = false;
    const loadAll = async () => {
      const next = {};
      await Promise.all(
        trackable.map(async (d) => {
          try {
            const r = await api.get(`/live/deployments/${d.bot_id}/history?limit=60`);
            next[d.bot_id] = r.data.points || [];
          } catch {
            next[d.bot_id] = [];
          }
        })
      );
      if (!cancelled) setHistoryByBot(next);
    };
    loadAll();
    const t = setInterval(loadAll, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [data]);

  useEffect(() => {
    if (location.search.includes("linked=1")) {
      setMsg({ tone: "ok", text: "cTrader demo account linked successfully. Ready for Phase 2b live-account read." });
      fetchOauthStatus();
    }
  }, [location.search, fetchOauthStatus]);

  const linkCtraderAccount = () => {
    // Full-page redirect so Spotware OAuth flow can complete
    window.location.href = `${api.defaults.baseURL}/oauth/spotware/start`;
  };

  const deployTop3 = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post("/live/deploy", { limit: 3, virtual_balance: 1000 });
      setMsg({ tone: "ok", text: `Deployed ${r.data.deployed} new · restarted ${r.data.restarted}` });
      fetchDeployments();
    } catch (e) {
      setMsg({ tone: "err", text: e?.response?.data?.detail || "Deploy failed" });
    } finally {
      setBusy(false);
    }
  };

  const stopBot = async (id, name) => {
    if (!window.confirm(`Stop ${name}? It will drop out of live paper trading.`)) return;
    try {
      await api.post(`/live/deployments/${id}/stop`);
      fetchDeployments();
    } catch (e) {
      setMsg({ tone: "err", text: e?.response?.data?.detail || "Stop failed" });
    }
  };

  const toggleAutoKill = async (ctid, next) => {
    try {
      await api.patch(`/ctrader/accounts/${ctid}/settings`, { auto_kill_enabled: next });
      fetchCtStatus();
    } catch (e) {
      setMsg({ tone: "err", text: e?.response?.data?.detail || "Toggle failed" });
    }
  };

  const resetBaseline = async (ctid) => {
    if (!window.confirm("Reset the drawdown baseline to the current balance? This re-arms the auto kill-switch.")) return;
    try {
      await api.patch(`/ctrader/accounts/${ctid}/settings`, { reset_baseline: true });
      fetchCtStatus();
    } catch (e) {
      setMsg({ tone: "err", text: e?.response?.data?.detail || "Reset failed" });
    }
  };

  if (!data) return <div className="p-6 text-[#64748B] mono">loading live desk…</div>;

  const deployments = data.deployments || [];

  return (
    <div data-testid="live-trading-page" className="px-4 sm:px-6 py-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Stage F · Paper Trading</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight">Live Trading Desk</h1>
          <p className="text-[#94A3B8] text-sm mt-1 max-w-2xl">
            Hall of Fame champions running as LIVE- tagged cBots inside cTrader on your VPS.
            $1,000 virtual balance each. Auto-killed if any bot drops <span className="text-[#FF4D4D] mono">−20%</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {oauthStatus && !oauthStatus.linked_count && oauthStatus.configured && (
            <button
              data-testid="link-ctrader-btn"
              onClick={linkCtraderAccount}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#60A5FA]/40 text-[#60A5FA] hover:bg-[#60A5FA]/10 rounded-md mono text-sm uppercase tracking-wider transition-colors"
            >
              <LinkSimpleIcon size={16} weight="fill" />
              Link cTrader
            </button>
          )}
          {oauthStatus && oauthStatus.linked_count > 0 && (
            <div
              data-testid="ctrader-linked-badge"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#D6FF38]/30 bg-[#D6FF38]/5 text-[#D6FF38] rounded-md mono text-xs uppercase tracking-wider"
            >
              <CheckCircleIcon size={14} weight="fill" />
              cTrader Linked
            </div>
          )}
          <button
            data-testid="deploy-top3-btn"
            onClick={deployTop3}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D6FF38] text-[#0B0C10] rounded-md mono text-sm uppercase tracking-wider hover:bg-[#c4ef2c] disabled:opacity-50 transition-colors"
          >
            <RocketLaunchIcon size={16} weight="fill" />
            {busy ? "Deploying…" : "Deploy Top 3"}
          </button>
        </div>
      </div>

      {msg && (
        <div
          data-testid="deploy-msg"
          className={`border rounded-md p-3 text-sm mono ${
            msg.tone === "ok"
              ? "border-[#D6FF38]/30 bg-[#D6FF38]/5 text-[#D6FF38]"
              : "border-[#FF4D4D]/30 bg-[#FF4D4D]/5 text-[#FF4D4D]"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Auto Kill-Switch Watchdog (Open API live monitoring) */}
      {ctStatus?.available && ctStatus.accounts?.length > 0 && (
        <div
          data-testid="watchdog-panel"
          className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${ctStatus.connected ? "bg-[#2ECA7F]" : "bg-[#FF4D4D]"}`}
                title={ctStatus.connected ? "TCP session live" : "Reconnecting…"}
              />
              <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/80">
                Kill-Switch Watchdog · Open API
              </div>
            </div>
            <div className="text-[10px] mono text-[#64748B]">
              triggers auto-close at −{ctStatus.auto_kill_dd_pct}% drawdown
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {ctStatus.accounts.map((a) => {
              const dd = Number(a.drawdown_pct || 0);
              const threshold = Number(ctStatus.auto_kill_dd_pct || 20);
              const ratio = Math.min(1, dd / threshold);
              const barColor = ratio >= 0.75 ? "#FF4D4D" : ratio >= 0.5 ? "#F97316" : "#2ECA7F";
              return (
                <div
                  key={a.ctid}
                  data-testid={`watchdog-account-${a.ctid}`}
                  className="border border-[#2A2D3A]/60 rounded-md p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="mono text-xs text-[#94A3B8]">
                      ctid <span className="text-white">{a.ctid}</span>
                      <span className="ml-2 text-[10px] uppercase" style={{ color: a.authenticated ? "#2ECA7F" : "#FF4D4D" }}>
                        {a.authenticated ? "authed" : "auth pending"}
                      </span>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer" title="Toggle auto kill-switch">
                      <input
                        data-testid={`autokill-toggle-${a.ctid}`}
                        type="checkbox"
                        checked={!!a.auto_kill_enabled}
                        onChange={(e) => toggleAutoKill(a.ctid, e.target.checked)}
                        className="h-3.5 w-3.5 accent-[#D6FF38]"
                      />
                      <span className="text-[10px] mono uppercase tracking-wider" style={{ color: a.auto_kill_enabled ? "#D6FF38" : "#64748B" }}>
                        auto kill {a.auto_kill_enabled ? "ARMED" : "off"}
                      </span>
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-xs mono">
                    <div>
                      <div className="text-[9px] uppercase text-[#64748B] tracking-widest">Baseline</div>
                      <div className="text-[#F1F5F9]">{fmt.money(a.baseline_balance)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-[#64748B] tracking-widest">Equity</div>
                      <div className="text-[#F1F5F9]">{fmt.money(a.equity || a.balance)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-[#64748B] tracking-widest">Drawdown</div>
                      <div style={{ color: barColor }}>−{dd.toFixed(2)}%</div>
                    </div>
                  </div>
                  {/* Drawdown progress bar */}
                  <div className="mt-2 h-1 bg-[#0B0C10] rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${ratio * 100}%`, background: barColor }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[10px] mono text-[#64748B]">
                    <span>{a.open_positions} open · {a.trades_count} closed</span>
                    <button
                      data-testid={`reset-baseline-${a.ctid}`}
                      onClick={() => resetBaseline(a.ctid)}
                      className="uppercase tracking-wider hover:text-[#D6FF38] transition-colors"
                    >
                      reset baseline
                    </button>
                  </div>
                  {a.auto_kill_fired_at && (
                    <div className="mt-2 text-[10px] mono text-[#FF4D4D]">
                      ⚠ auto-kill fired at {new Date(a.auto_kill_fired_at).toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Portfolio Risk snapshot (V2 P4 lightweight portfolio manager) */}
      <PortfolioRiskCard deployments={deployments} />

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Live Bots" value={data.running_count} sub={`${data.count} total deployments`} />
        <StatCard
          label="Aggregate P&L"
          value={`${data.total_pnl >= 0 ? "+" : ""}${fmt.money(data.total_pnl)}`}
          tone={data.total_pnl >= 0 ? "profit" : "loss"}
          sub="across all RUNNING bots"
        />
        <StatCard label="Total Balance" value={fmt.money(data.total_balance)} sub={`from $${data.running_count * 1000} start`} />
        <StatCard label="Killed" value={data.killed_count} sub="auto-killed or manual" tone={data.killed_count > 0 ? "loss" : ""} />
      </div>

      {/* Empty state */}
      {deployments.length === 0 && (
        <div data-testid="live-empty" className="border border-dashed border-[#2A2D3A] rounded-md p-12 text-center">
          <RocketLaunchIcon size={32} className="mx-auto text-[#64748B]" />
          <div className="text-[#94A3B8] mono text-sm mt-3">No live deployments yet.</div>
          <div className="text-[#64748B] mono text-xs mt-1 max-w-md mx-auto">
            Click <span className="text-[#D6FF38]">Deploy Top 3</span> to promote Hall of Fame
            champions into paper trading. Your VPS <span className="text-[#F1F5F9]">Deploy_Live.ps1</span> will
            clone the cBots on the next Run_Forever cycle.
          </div>
        </div>
      )}

      {/* Deployments table */}
      {deployments.length > 0 && (
        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="live-deployments-table">
            <thead className="text-[10px] mono uppercase tracking-widest text-[#64748B] border-b border-[#2A2D3A]">
              <tr>
                <th className="text-left px-4 py-3">Bot</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Symbol</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-right px-4 py-3">P&L</th>
                <th className="text-center px-4 py-3 hidden md:table-cell">Trend</th>
                <th className="text-right px-4 py-3 hidden md:table-cell">Trades</th>
                <th className="text-right px-4 py-3 hidden lg:table-cell">Positions</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map((d) => {
                const meta = STATUS_META[d.status] || DEFAULT_META;
                const Icon = meta.Icon;
                // Prefer per-bot Open API attribution (V2 P6b) when we
                // matched this deployment to a specific label/symbol slice
                // via cTrader. Fallback order:
                //   1. bot_* (this bot's own trades from Open API)
                //   2. live_* (whole-account aggregate from Open API)
                //   3. VPS live_bridge.py push (current_*)
                const hasBotAttr = d.bot_attribution != null;
                const hasLive = d.live_snapshotted_at != null;
                const balance = hasLive ? d.live_balance : d.current_balance;
                const pnl = hasBotAttr
                  ? Number((d.bot_realized_pnl || 0) + (d.bot_unrealized_pnl || 0))
                  : Number(hasLive ? d.live_unrealized_pnl : d.current_pnl || 0);
                const openPos = hasBotAttr
                  ? d.bot_open_positions
                  : (hasLive ? d.live_open_positions : (d.open_positions || 0));
                const tradesCount = hasBotAttr
                  ? d.bot_trades_count
                  : (hasLive ? d.live_trades_count : (d.trades_count || 0));
                const pnlTone = pnl >= 0 ? "#2ECA7F" : "#FF4D4D";
                return (
                  <tr
                    key={d.id}
                    data-testid={`live-row-${d.bot_id}`}
                    className="border-b border-[#1F2230] hover:bg-[#1A1D26] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Link to={`/bot/${d.bot_id}`} className="mono text-white hover:text-[#D6FF38]">
                        {d.bot_name}
                      </Link>
                      <div className="text-[10px] mono text-[#64748B] mt-0.5">
                        rank #{d.hof_rank} · deployed {d.deployed_at ? new Date(d.deployed_at).toLocaleDateString() : "—"}
                        {hasBotAttr && d.bot_attribution === "label" && (
                          <span className="ml-2 text-[#D6FF38]" title="Attributed by cBot label match">
                            · label matched
                          </span>
                        )}
                        {hasBotAttr && d.bot_attribution === "symbol" && (
                          <span className="ml-2 text-[#F97316]" title="Attributed by symbol (label was empty - add Label = InstanceId to your cBot)">
                            · symbol matched
                          </span>
                        )}
                        {!hasBotAttr && hasLive && (
                          <span className="ml-2 text-[#94A3B8]" title="Showing account-wide aggregate">
                            · aggregate
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell mono text-xs text-[#94A3B8]">{d.symbol || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-1.5" title={meta.hint}>
                        <Icon size={14} weight="fill" style={{ color: meta.color }} />
                        <span className="mono text-[11px] uppercase tracking-wider" style={{ color: meta.color }}>
                          {meta.label}
                        </span>
                      </div>
                      {d.kill_reason && (
                        <div className="text-[10px] mono text-[#FF4D4D] mt-0.5">{d.kill_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 mono text-right">{fmt.money(balance)}</td>
                    <td className="px-4 py-3 mono text-right" style={{ color: pnlTone }}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {pnl >= 0 ? <TrendUpIcon size={12} /> : <TrendDownIcon size={12} />}
                        {pnl >= 0 ? "+" : ""}{fmt.money(pnl)}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell" data-testid={`sparkline-cell-${d.bot_id}`}>
                      <div className="flex justify-center">
                        <Sparkline points={historyByBot[d.bot_id] || []} />
                      </div>
                    </td>
                    <td className="px-4 py-3 mono text-right hidden md:table-cell text-[#94A3B8]">{tradesCount}</td>
                    <td className="px-4 py-3 mono text-right hidden lg:table-cell text-[#94A3B8]">{openPos}</td>
                    <td className="px-4 py-3 text-right">
                      {d.status === "RUNNING" && (
                        <button
                          data-testid={`stop-btn-${d.bot_id}`}
                          onClick={() => stopBot(d.id, d.bot_name)}
                          className="text-[11px] mono uppercase tracking-wider text-[#FF4D4D] hover:text-[#ff6666] border border-[#FF4D4D]/30 hover:border-[#FF4D4D]/60 px-2 py-1 rounded transition-colors"
                        >
                          Stop
                        </button>
                      )}
                      {d.status === "PENDING" && (
                        <span className="text-[10px] mono text-[#FFB400]">awaiting VPS</span>
                      )}
                      {d.status === "KILLED" && (
                        <PlayCircleIcon
                          size={18}
                          className="text-[#94A3B8] hover:text-[#D6FF38] cursor-pointer inline"
                          onClick={deployTop3}
                          title="Re-deploy top 3 (this bot will restart if still in Hall of Fame)"
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11px] mono text-[#64748B] text-center">
        Data refreshes every 15s · VPS <span className="text-[#F1F5F9]">live_bridge.py</span> pushes P&L each Run_Forever cycle
      </div>
    </div>
  );
}
